import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { ConfigContext } from '../../../context';
import { useMeReferralCodeQuery } from '../../../services';
import { getDeepLinkUrl } from '../../utils';
import { Button, ButtonType } from '../Button';
import { DeeplinkType } from '../Deeplink';
import { Modal } from '../Modal';
import { ShareUrlViaSocial } from '../ShareUrlViaSocial';

import classes from './ShareReferral.module.scss';

interface ShareReferralProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShareReferral: FunctionComponent<ShareReferralProps> = memo(({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const { data: referralCode, isFetching } = useMeReferralCodeQuery(undefined, { skip: !isOpen });

  const {
    shareReferralViaEmailSubjectLocaliseKey = '',
    shareReferralViaEmailBodyLocaliseKey = '',
  } = {
    ...useContext(ConfigContext).config.elements.referral,
  };

  const url = useMemo(
    () => getDeepLinkUrl({ type: DeeplinkType.REFERRAL, referralCode }),
    [referralCode]
  );

  const copyClick = useCallback(async () => {
    onClose();

    if (!navigator.clipboard || !url) {
      return;
    }

    await navigator.clipboard.writeText(url);

    toast(t('shareReferral.link-copied'));
  }, [onClose, t, url]);

  const title = t('shareReferral.title');

  const body = useMemo(() => {
    if (isFetching) {
      return (
        <div className={classes['loader']}>
          <Skeleton height={'2.5rem'} />
          <Skeleton height={'2.5rem'} />
          <Skeleton height={'2.5rem'} />
        </div>
      );
    }

    return (
      <div className={classes['share-referral']}>
        <div className={classes['share-referral__title']}>{title}</div>

        <ShareUrlViaSocial
          url={url}
          subject={t(shareReferralViaEmailSubjectLocaliseKey)}
          body={`${t(shareReferralViaEmailBodyLocaliseKey)}\n`}
        />

        <Button
          type={ButtonType.secondary}
          label={t('shareReferral.copy-link')}
          onClick={copyClick}
          fullWidth
        />
      </div>
    );
  }, [
    copyClick,
    isFetching,
    shareReferralViaEmailBodyLocaliseKey,
    shareReferralViaEmailSubjectLocaliseKey,
    t,
    title,
    url,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      body={body}
      onClose={onClose}
      contentStyle={{ width: '26rem' }}
      ariaLabel={title}
      disableCloseByOverlay
    />
  );
});
